import { TeButton, TeButtonOutline, TeContainer, TeContainerSlim } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';

import { DeclineAgreementButton } from './DeclineAgreementButton';
import useServiceAgreementId from '../../hooks/useServiceAgreementId';
import { useServiceAgreementWithId } from '../../pages/Customers/customers.queries';
import { ServiceAgreementStatusEnum } from '../../api/generated/pm';

interface ServiceAgreementStepActionsProps {
  step: 'companyReview' | 'agreementReview' | 'termsAndConditions';
  id: string;
  onNextStep: () => void;
  onPreviousStep?: () => void;
  isNextStepDisabled?: boolean;
  onReject: () => void;
}

function ServiceAgreementStepActions({
  step,
  id,
  onNextStep,
  onPreviousStep,
  onReject,
  isNextStepDisabled,
}: ServiceAgreementStepActionsProps) {
  const { t } = useTranslation();
  const { data: serviceAgreement } = useServiceAgreementWithId(id);
  const { acceptAgreement, isPendingAcceptServiceAgreement } = useServiceAgreementId(id);

  const handleClickAcceptAgreement = async () => {
    await acceptAgreement();
    onNextStep();
  };

  if (!serviceAgreement) return;

  const isAlreadyProcessed =
    serviceAgreement.serviceAgreementStatus === ServiceAgreementStatusEnum.ACTIVE ||
    serviceAgreement.serviceAgreementStatus === ServiceAgreementStatusEnum.DECLINED;

  if (step === 'companyReview')
    return (
      <TeContainer
        gap="xs"
        fill
        grow
        justifyContent="space-between"
        alignItems="center"
        padding="m"
      >
        <DeclineAgreementButton
          id={id}
          disabled={isAlreadyProcessed}
          onReject={onReject}
          company={serviceAgreement.provider!}
        />
        <TeButton text={t('nextStep')} size="s" teclick={onNextStep} />
      </TeContainer>
    );

  if (step === 'agreementReview') {
    return (
      <TeContainer
        gap="xs"
        fill
        grow
        justifyContent="space-between"
        alignItems="center"
        padding="m"
      >
        <DeclineAgreementButton
          id={id}
          disabled={isAlreadyProcessed}
          onReject={onReject}
          company={serviceAgreement.provider!}
        />
        <TeContainerSlim gap="xs">
          <TeButtonOutline
            text={t('previousStep')}
            size="s"
            variant="neutral"
            teclick={onPreviousStep}
            disabled={isPendingAcceptServiceAgreement}
          />
          <TeButton
            text={t('nextStep')}
            size="s"
            teclick={onNextStep}
            disabled={isNextStepDisabled}
          />
        </TeContainerSlim>
      </TeContainer>
    );
  }
  return (
    <TeContainer gap="xs" fill grow justifyContent="space-between" alignItems="center" padding="m">
      <DeclineAgreementButton
        id={id}
        disabled={isPendingAcceptServiceAgreement || isAlreadyProcessed}
        onReject={onReject}
        company={serviceAgreement.provider!}
      />
      <TeContainerSlim gap="xs">
        <TeButtonOutline
          text={t('previousStep')}
          size="s"
          variant="neutral"
          teclick={onPreviousStep}
          disabled={isPendingAcceptServiceAgreement}
        />
        <TeButton
          text={t('serviceAgreement.accept')}
          size="s"
          teclick={handleClickAcceptAgreement}
          loading={isPendingAcceptServiceAgreement}
          disabled={isPendingAcceptServiceAgreement || isNextStepDisabled || isAlreadyProcessed}
        />
      </TeContainerSlim>
    </TeContainer>
  );
}

export { ServiceAgreementStepActions };
